import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// @ts-expect-error - cartesian-dist-min ships without types in some versions
import Plotly from "plotly.js-cartesian-dist-min";
import type * as PlotlyTypes from "plotly.js";
import createPlotlyComponent from "react-plotly.js/factory";
import type { NeighborFlavor } from "@/utils/types/flavor";
import type { TrailPoint } from "@/hooks/useFlavorSpaceTrail";

const Plot = createPlotlyComponent(Plotly as unknown as Parameters<typeof createPlotlyComponent>[0]);

// Comparison overlay payload — each entry is rendered as one coloured dot
// with a leader line + persistent name label. Kept for API parity with the
// pre-Plotly component.
export type ScatterComparison = {
  id: string;
  name: string;
  color: string;
  x: number;
  y: number;
};

type Point = { x: number; y: number };

// Neighbor shape exposed by the new wire (apps/nos/client/src/services/
// workbench_api.ts:VizPayload.neighbors). The component accepts EITHER the
// new wire shape (family object + isNearest + isLabeled) OR the legacy
// NeighborFlavor shape so DevPlots keeps compiling.
export type ScatterNeighborInput =
  | NeighborFlavor
  | {
      id: string;
      name: string;
      family: { code: string; label: string; color: string } | null;
      x: number;
      y: number;
      isNearest?: boolean;
      isLabeled?: boolean;
      /**
       * T15: optional weighted L² loss to the target (only present for
       * target_pca / taste_2d projections). DevPlots legacy shape omits
       * this field — ScatterPlot treats its absence as null (no rings).
       */
      loss?: number | null;
    };

type ScatterPlotProps = {
  neighbors: ScatterNeighborInput[];
  /** null when the flavor has no target food yet — no Target icon is rendered. */
  target: Point | null;
  /** null when the formulation is empty — no Current marker is rendered. */
  current: Point | null;
  comparisons?: ScatterComparison[];
  width?: number;
  height?: number;
  targetName?: string;
  currentName?: string;
  /**
   * Optional override for the Current marker's fill color. Used by the
   * Auto-Formulate Review state to render the proposed formulation in
   * orange (signalling "temporary; can be reverted"). When unset, the
   * marker uses `--accent` (brand-lime) as before.
   */
  currentColor?: string;
  /** Stroke color paired with `currentColor`. Defaults to a darker shade. */
  currentColorInk?: string;
  /**
   * Called when the user clicks one of the neighbour-flavour dots. Fires
   * only for actual neighbour clicks — target/current/comparison markers
   * are filtered out via Plotly's customdata. Used for analytics today;
   * future UX (drill-into-neighbour) can hook in here too.
   */
  onFlavorClick?: (n: { id: string; name: string }) => void;
  /**
   * Outermost iso-loss ring radius in data units (T15). When provided, the
   * symmetric fit ensures all rings stay inside the visible panel.
   * Omit until T15 lands — purely additive, no behavior change when absent.
   */
  maxRingRadius?: number;
  /**
   * T16: path trail of the Current marker's past positions. When provided,
   * renders a fading breadcrumb trail behind Current. Omit for no trail.
   * Produced by `useFlavorSpaceTrail` — only pass from the Workbench
   * SensoryAnalysisRail mount; leave other call sites (DevPlots) trail-less.
   */
  trail?: TrailPoint[];
};

// ─── Iso-loss ring helpers (T15) ────────────────────────────────────────────
// Kept inline here (rather than a separate IsoLossRings.tsx file) because the
// ring shapes are ~20 lines of layout data assembled directly into Plotly's
// `shapes` + `annotations` arrays — no JSX, no new component needed.

function niceRound(v: number): number {
  if (v <= 0) return 0;
  const mag = 10 ** Math.floor(Math.log10(v));
  const n = v / mag;
  const rounded = n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10;
  return rounded * mag;
}

function computeRingRadii(losses: number[]): number[] {
  if (losses.length === 0) return [];
  const sorted = [...losses].sort((a, b) => a - b);
  const pcts = [0.25, 0.5, 0.75, 0.95];
  const seen = new Set<number>();
  const radii: number[] = [];
  for (const p of pcts) {
    const v = sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))]!;
    const r = niceRound(v);
    if (r > 0 && !seen.has(r)) { seen.add(r); radii.push(r); }
  }
  return radii.sort((a, b) => a - b);
}

// Fallback color for foods with no family classification yet.
const DEFAULT_FAMILY_COLOR = "#cfcec3";

function familyColor(n: ScatterNeighborInput): string {
  if ("family" in n && n.family && typeof n.family === "object" && "color" in n.family) {
    return n.family.color;
  }
  return DEFAULT_FAMILY_COLOR;
}

function isNearest(n: ScatterNeighborInput): boolean {
  return "isNearest" in n && n.isNearest === true;
}

function isLabeled(n: ScatterNeighborInput): boolean {
  return "isLabeled" in n && n.isLabeled === true;
}

// ─── CSS-token bridge ───────────────────────────────────────────────────────
// Plotly's SVG output writes colours as presentation attributes (fill="…",
// stroke="…") which don't resolve CSS custom-properties. We read the actual
// hex values off the documentElement at mount and re-read whenever the theme
// attributes change.

type ThemeTokens = {
  ink:        string;
  ink2:       string;
  ink3:       string;
  ink4:       string;
  accent:     string;
  accentInk:  string;
  bgSunk:     string;
  panel:      string;
  line:       string;
  line2:      string;
  lineStrong: string;
};

function readToken(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function readThemeTokens(): ThemeTokens {
  return {
    ink:        readToken("--ink",          "#1f2330"),
    ink2:       readToken("--ink-2",        "#4a4f5e"),
    ink3:       readToken("--ink-3",        "#6e7385"),
    ink4:       readToken("--ink-4",        "#a3a6b2"),
    accent:     readToken("--accent",       "#3a6b3c"),
    accentInk:  readToken("--accent-ink",   "#274f29"),
    bgSunk:     readToken("--bg-sunk",      "#f2f0e8"),
    panel:      readToken("--panel",        "#fbfaf4"),
    line:       readToken("--line",         "#d6d3c7"),
    line2:      readToken("--line-2",       "#e6e3d9"),
    lineStrong: readToken("--line-strong",  "#cfcec3"),
  };
}

function useThemeTokens(): ThemeTokens {
  const [tokens, setTokens] = useState<ThemeTokens>(readThemeTokens);
  useEffect(() => {
    // Re-read on theme/density attribute changes flipped by useTheme.
    const obs = new MutationObserver(() => setTokens(readThemeTokens()));
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme", "data-density", "style"],
    });
    return () => obs.disconnect();
  }, []);
  return tokens;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ScatterPlot({
  neighbors,
  target,
  current,
  comparisons = [],
  width = 344,
  height = 280,
  targetName = "Target",
  currentName = "Current",
  currentColor,
  currentColorInk,
  onFlavorClick,
  maxRingRadius,
  trail,
}: ScatterPlotProps) {
  const plotRef = useRef<HTMLDivElement | null>(null);
  const tokens = useThemeTokens();

  // ── Iso-loss ring radii (T15) ─────────────────────────────────────────────
  // Collect valid losses from neighbors (non-null, > 0) and compute
  // percentile-based niceRound'd radii for 3–4 concentric rings.
  const ringRadii = useMemo(
    () => computeRingRadii(
      neighbors
        .map((n) => ("loss" in n ? (n.loss ?? 0) : 0))
        .filter((v) => v > 0),
    ),
    [neighbors],
  );
  // Feed the outermost ring radius into computedFit so it pads the viewport.
  const maxRingRadiusComputed =
    ringRadii.length > 0 ? ringRadii[ringRadii.length - 1]! : undefined;
  // If a maxRingRadius prop was passed explicitly, use the larger of the two.
  const effectiveMaxRingRadius =
    maxRingRadius !== undefined && maxRingRadiusComputed !== undefined
      ? Math.max(maxRingRadius, maxRingRadiusComputed)
      : maxRingRadius ?? maxRingRadiusComputed;

  // ── Auto-fit ─────────────────────────────────────────────────────────────
  // Symmetric around origin. Under target_pca / taste_2d projections,
  // Target sits at (0, 0) by construction — placing it at the geometric
  // center of the panel makes the iso-loss rings (T15) read as concentric
  // around Target, and the path trail (T16) reads as a breadcrumb spiraling
  // toward origin.
  //
  // The FIT is recomputed whenever inputs change, but it's only APPLIED
  // to the plot's axes on (a) first render, (b) empty → non-empty
  // transition. After that the view stays still so the user can track
  // Current's motion without the plot pan/zoom shifting under them.
  // User-initiated pan/zoom is captured below via onRelayout and wins
  // until the next transition.
  const currentX = current?.x ?? null;
  const currentY = current?.y ?? null;
  const hasCurrentForFit = currentX !== null && currentY !== null;
  const computedFit = useMemo(() => {
    // Symmetric around origin. Under target_pca / taste_2d projections,
    // Target sits at (0, 0) by construction — placing it at the geometric
    // center of the panel makes the iso-loss rings (T15) read as
    // concentric around Target, and the path trail (T16) reads as a
    // breadcrumb spiraling toward origin.
    //
    // Pad = max(|coord|, max ring radius) * 1.08 across all rendered
    // points + the outermost ring. The 1.08 multiplier matches the
    // dashboard reference (`build_dashboard.py` _wbLoadTargetPCA).
    let maxAbs = 0;
    for (const n of neighbors) {
      if (Math.abs(n.x) > maxAbs) maxAbs = Math.abs(n.x);
      if (Math.abs(n.y) > maxAbs) maxAbs = Math.abs(n.y);
    }
    if (currentX !== null && Math.abs(currentX) > maxAbs) maxAbs = Math.abs(currentX);
    if (currentY !== null && Math.abs(currentY) > maxAbs) maxAbs = Math.abs(currentY);
    if (effectiveMaxRingRadius !== undefined && effectiveMaxRingRadius > maxAbs) maxAbs = effectiveMaxRingRadius;
    const pad = (maxAbs || 1) * 1.08;
    return {
      xrange: [-pad, pad] as [number, number],
      yrange: [-pad, pad] as [number, number],
    };
  }, [currentX, currentY, neighbors, effectiveMaxRingRadius]);

  // Pinned view range. Updates on:
  //   1. First render (useState initializer pulls from `computedFit`)
  //   2. Empty → non-empty transition (useEffect detects edge, applies new fit)
  //   3. User pan/zoom (onRelayout captures and stores the new range)
  const [viewRange, setViewRange] = useState<{
    xrange: [number, number];
    yrange: [number, number];
  }>(computedFit);
  const prevHasCurrent = useRef<boolean>(hasCurrentForFit);
  useEffect(() => {
    if (!prevHasCurrent.current && hasCurrentForFit) {
      setViewRange(computedFit);
    }
    prevHasCurrent.current = hasCurrentForFit;
  }, [hasCurrentForFit, computedFit]);

  const handleRelayout = useCallback((event: PlotlyTypes.PlotRelayoutEvent) => {
    // Plotly's relayout fires when the user pans or scroll-zooms. Capture
    // the new range so subsequent React re-renders don't blow it away
    // (Plotly.react treats layout.xaxis.range as authoritative; without
    // this capture, the next prop update would snap back to the pinned
    // range and erase the user's view).
    const e = event as Record<string, number | undefined>;
    const x0 = e["xaxis.range[0]"];
    const x1 = e["xaxis.range[1]"];
    const y0 = e["yaxis.range[0]"];
    const y1 = e["yaxis.range[1]"];
    if (
      typeof x0 === "number" && typeof x1 === "number" &&
      typeof y0 === "number" && typeof y1 === "number"
    ) {
      setViewRange({ xrange: [x0, x1], yrange: [y0, y1] });
    }
  }, []);

  const xrange = viewRange.xrange;
  const yrange = viewRange.yrange;

  // ── Neighbor traces ─────────────────────────────────────────────────────
  // Three buckets + always-on labels for the top 3:
  //   others (non-isNearest)   — size ~10 (70% of nearest), opacity 0.3
  //                              rendered behind everything else so the
  //                              user keeps a stable visual map of the
  //                              full flavor space across saves
  //   nearest top 4..K         — size 14, opacity 0.7
  //   nearest top 3 (labeled)  — size 14, opacity 1.0 + leader line + text
  //
  // Labels and leader lines are emitted as scatter `text` and `lines`
  // traces (NOT layout annotations) so they clip with the plot area when
  // the user pans/zooms. Leader length is ~22px regardless of plot zoom
  // — pixel→data conversion uses the active range + plot dimensions.
  // Text sits at the leader endpoint, with anchor side picked from the
  // labeled-set centroid.
  const LEADER_PX = 22;
  const { nearestTrace, labeledTrace, leaderTrace, labelTextTrace, othersTrace } = useMemo(() => {
    const nearest  = { x: [] as number[], y: [] as number[], color: [] as string[], text: [] as string[], ids: [] as string[] };
    const labeled  = { x: [] as number[], y: [] as number[], color: [] as string[], text: [] as string[], ids: [] as string[] };
    const others   = { x: [] as number[], y: [] as number[], color: [] as string[], text: [] as string[], ids: [] as string[] };

    for (const n of neighbors) {
      const c = familyColor(n);
      if (!isNearest(n)) {
        others.x.push(n.x);
        others.y.push(n.y);
        others.color.push(c);
        others.text.push(n.name);
        others.ids.push(n.id);
        continue;
      }
      if (isLabeled(n)) {
        labeled.x.push(n.x);
        labeled.y.push(n.y);
        labeled.color.push(c);
        labeled.text.push(n.name);
        labeled.ids.push(n.id);
      } else {
        nearest.x.push(n.x);
        nearest.y.push(n.y);
        nearest.color.push(c);
        nearest.text.push(n.name);
        nearest.ids.push(n.id);
      }
    }

    const leaderX: (number | null)[] = [];
    const leaderY: (number | null)[] = [];
    const labelEndX: number[] = [];
    const labelEndY: number[] = [];
    const labelTexts: string[] = [];
    const labelPositions: string[] = [];
    if (labeled.x.length > 0) {
      const cx = labeled.x.reduce((s, v) => s + v, 0) / labeled.x.length;
      const cy = labeled.y.reduce((s, v) => s + v, 0) / labeled.y.length;

      // Pixel→data conversion. Uses the auto-fit span and the inner plot
      // dimensions (panel width/height minus the axis margins we set in
      // `layout` below). Collision resolution runs in pixel space, then
      // we convert endpoints back to data coords for the trace.
      const xSpan = xrange[1]! - xrange[0]!;
      const ySpan = yrange[1]! - yrange[0]!;
      const innerW = Math.max(1, width - 28);
      const innerH = Math.max(1, height - 28);
      const dataPerPxX = xSpan / innerW;
      const dataPerPxY = ySpan / innerH;
      const dataToPxX = (d: number) => (d - xrange[0]!) / dataPerPxX;
      const dataToPxY = (d: number) => (yrange[1]! - d) / dataPerPxY; // y inverted
      const pxToDataX = (p: number) => xrange[0]! + p * dataPerPxX;
      const pxToDataY = (p: number) => yrange[1]! - p * dataPerPxY;

      // Pixel bbox estimate for the label text. 10.5-px font, char widths
      // vary by glyph so we use an average; the small +PAD buffer covers
      // the slack and gives a comfortable air gap between adjacent labels.
      const CHAR_PX = 5.6;
      const LINE_PX = 14;
      const PAD_PX = 4;

      type Place = {
        i: number;
        dotPxX: number;
        dotPxY: number;
        endPxX: number;
        endPxY: number;
        textDir: 1 | -1; // +1 = text extends right of endpoint, -1 = left
        textW: number;   // pixel width of the text run
      };

      const places: Place[] = [];
      for (let i = 0; i < labeled.x.length; i++) {
        const px = labeled.x[i]!;
        const py = labeled.y[i]!;
        let dx = px - cx;
        let dy = py - cy;
        const len = Math.hypot(dx, dy);
        if (len < 1e-6) { dx = 0; dy = 1; }
        else { dx /= len; dy /= len; }
        const dotPxX = dataToPxX(px);
        const dotPxY = dataToPxY(py);
        const endPxX = dotPxX + dx * LEADER_PX;
        const endPxY = dotPxY - dy * LEADER_PX; // data-y up → pixel-y down
        places.push({
          i,
          dotPxX,
          dotPxY,
          endPxX,
          endPxY,
          textDir: dx >= 0 ? 1 : -1,
          textW: labeled.text[i]!.length * CHAR_PX,
        });
      }

      // Bbox helper. Text anchor is at endPx; for "middle right" the
      // text runs to the right of the anchor; for "middle left" to the
      // left. Padded so adjacent labels keep an air gap.
      const bbox = (p: Place) => ({
        minX: p.endPxX + (p.textDir === 1 ? 0 : -p.textW) - PAD_PX,
        maxX: p.endPxX + (p.textDir === 1 ? p.textW : 0) + PAD_PX,
        minY: p.endPxY - LINE_PX / 2 - PAD_PX,
        maxY: p.endPxY + LINE_PX / 2 + PAD_PX,
      });

      // Iterative collision resolution. For each overlapping pair, push
      // the two labels apart along the smaller-overlap axis (cheapest
      // way to clear the collision). 6 passes is plenty for ≤3 labels;
      // early-out the moment a full sweep finds no overlaps.
      const ITER = 6;
      for (let iter = 0; iter < ITER; iter++) {
        let anyOverlap = false;
        for (let a = 0; a < places.length; a++) {
          for (let b = a + 1; b < places.length; b++) {
            const A = bbox(places[a]!);
            const B = bbox(places[b]!);
            const overlapX = Math.min(A.maxX, B.maxX) - Math.max(A.minX, B.minX);
            const overlapY = Math.min(A.maxY, B.maxY) - Math.max(A.minY, B.minY);
            if (overlapX > 0 && overlapY > 0) {
              anyOverlap = true;
              const acx = (A.minX + A.maxX) / 2;
              const bcx = (B.minX + B.maxX) / 2;
              const acy = (A.minY + A.maxY) / 2;
              const bcy = (B.minY + B.maxY) / 2;
              if (overlapX < overlapY) {
                const dir = acx < bcx ? -1 : 1;
                const half = overlapX / 2 + 1;
                places[a]!.endPxX += dir * half;
                places[b]!.endPxX -= dir * half;
              } else {
                const dir = acy < bcy ? -1 : 1;
                const half = overlapY / 2 + 1;
                places[a]!.endPxY += dir * half;
                places[b]!.endPxY -= dir * half;
              }
            }
          }
        }
        if (!anyOverlap) break;
      }

      for (const p of places) {
        const exData = pxToDataX(p.endPxX);
        const eyData = pxToDataY(p.endPxY);
        // Each leader line: [dot, endpoint, null] so a single trace
        // covers all leaders without visually joining them.
        leaderX.push(labeled.x[p.i]!, exData, null);
        leaderY.push(labeled.y[p.i]!, eyData, null);
        labelEndX.push(exData);
        labelEndY.push(eyData);
        labelTexts.push(labeled.text[p.i]!);
        labelPositions.push(p.textDir === 1 ? "middle right" : "middle left");
      }
    }

    return {
      othersTrace: {
        type: "scatter" as const,
        mode: "markers" as const,
        x: others.x,
        y: others.y,
        text: others.text,
        customdata: others.ids,
        marker: {
          size: 10,
          color: others.color,
          opacity: 0.3,
          line: { width: 0, color: tokens.panel },
        },
        hovertemplate: "%{text}<extra></extra>",
        showlegend: false,
        cliponaxis: true,
      },
      nearestTrace: {
        type: "scatter" as const,
        mode: "markers" as const,
        x: nearest.x,
        y: nearest.y,
        text: nearest.text,
        customdata: nearest.ids,
        marker: {
          size: 14,
          color: nearest.color,
          opacity: 0.7,
          line: { width: 1, color: tokens.panel },
        },
        hovertemplate: "%{text}<extra></extra>",
        showlegend: false,
        cliponaxis: true,
      },
      labeledTrace: {
        type: "scatter" as const,
        mode: "markers" as const,
        x: labeled.x,
        y: labeled.y,
        text: labeled.text,
        customdata: labeled.ids,
        marker: {
          size: 14,
          color: labeled.color,
          opacity: 1.0,
          line: { width: 1.2, color: tokens.panel },
        },
        hovertemplate: "%{text}<extra></extra>",
        showlegend: false,
        cliponaxis: true,
      },
      leaderTrace: {
        type: "scatter" as const,
        mode: "lines" as const,
        x: leaderX as unknown as number[],
        y: leaderY as unknown as number[],
        line: { color: tokens.ink4, width: 0.7 },
        opacity: 0.6,
        hoverinfo: "skip" as const,
        showlegend: false,
        cliponaxis: true,
      },
      labelTextTrace: {
        type: "scatter" as const,
        mode: "text" as const,
        x: labelEndX,
        y: labelEndY,
        text: labelTexts,
        // Per-point textposition array — Plotly accepts at runtime; cast
        // through unknown to satisfy @types/plotly.js's single-string union.
        textposition: labelPositions as unknown as PlotlyTypes.PlotData["textposition"],
        textfont: { size: 10.5, color: tokens.ink2 },
        hoverinfo: "skip" as const,
        showlegend: false,
        cliponaxis: true,
      },
    };
  }, [neighbors, tokens.panel, tokens.ink2, tokens.ink4, xrange, yrange, width, height]);

  // ── Target marker (design: halo + open ring with crosshair + center dot) ─
  // Three stacked traces.
  //   • Halo: soft ink-tinted halo (~28px).
  //   • Ring + crosshair: Plotly's built-in "circle-cross-open" symbol which
  //     combines an open ring and a + sign in one marker. Replaces the prior
  //     pixel-sizemode shape lines (those aren't reliable in cartesian-dist-min).
  //   • Center dot: small filled ink dot.
  //
  // Empty x/y arrays when target is null (no target food). Keeps the data[]
  // ordering stable for Plotly diffing so the trace slot doesn't blink.
  const hasTarget = target !== null;
  const targetHaloTrace = {
    type: "scatter" as const,
    mode: "markers" as const,
    x: hasTarget ? [target!.x] : [],
    y: hasTarget ? [target!.y] : [],
    marker: { size: 32, color: tokens.ink, opacity: 0.12 },
    hoverinfo: "skip" as const,
    showlegend: false,
    cliponaxis: true,
  };
  const targetRingTrace = {
    type: "scatter" as const,
    mode: "markers" as const,
    x: hasTarget ? [target!.x] : [],
    y: hasTarget ? [target!.y] : [],
    text: hasTarget ? [targetName] : [],
    marker: {
      size: 22,
      symbol: "circle-cross-open" as const,
      color: tokens.ink,
      line: { width: 1.4, color: tokens.ink },
    },
    hovertemplate: "%{text}<extra></extra>",
    showlegend: false,
    cliponaxis: true,
  };
  const targetDotTrace = {
    type: "scatter" as const,
    mode: "markers" as const,
    x: hasTarget ? [target!.x] : [],
    y: hasTarget ? [target!.y] : [],
    marker: { size: 5, color: tokens.ink },
    hoverinfo: "skip" as const,
    showlegend: false,
    cliponaxis: true,
  };

  // ── Current marker (design: soft halo + filled dot with stroke) ─────────
  // Empty formulation: current is null. Use empty x/y arrays so the trace
  // renders nothing — keeps the data[] layout stable for Plotly diffing
  // (avoids a destructive re-mount on the trace ordering).
  const hasCurrent = current !== null;
  // Effective current-marker colors: prop overrides win over the theme
  // accent. The Auto-Formulate Review state uses this to render the
  // proposed-formulation dot in orange.
  const effCurrentColor = currentColor ?? tokens.accent;
  const effCurrentColorInk = currentColorInk ?? tokens.accentInk;
  const currentHaloTrace = {
    type: "scatter" as const,
    mode: "markers" as const,
    x: hasCurrent ? [current!.x] : [],
    y: hasCurrent ? [current!.y] : [],
    marker: { size: 24, color: effCurrentColor, opacity: 0.22 },
    hoverinfo: "skip" as const,
    showlegend: false,
  };
  const currentTrace = {
    type: "scatter" as const,
    mode: "markers" as const,
    x: hasCurrent ? [current!.x] : [],
    y: hasCurrent ? [current!.y] : [],
    text: hasCurrent ? [currentName] : [],
    marker: {
      size: 12,
      color: effCurrentColor,
      line: { width: 1.4, color: effCurrentColorInk },
    },
    hovertemplate: "%{text}<extra></extra>",
    showlegend: false,
  };

  // ── Path trail traces (T16) ──────────────────────────────────────────────
  // One short line segment per consecutive trail pair, each with its own
  // monotonically increasing opacity (oldest → 0.15, newest → 0.85). ≤ 20
  // traces — well within Plotly's budget. Plus one marker trace for the
  // breadcrumb dots (per-point size + opacity arrays). The newest trail
  // point sits at the same coord as Current, so we drop it from the dot
  // trace — the live Current marker visually "completes" the trail.
  const trailTraces = useMemo(() => {
    if (!trail || trail.length < 1) return [];

    // Line segments — one trace per consecutive pair.
    const lineTraces: Partial<PlotlyTypes.PlotData>[] = [];
    for (let i = 0; i < trail.length - 1; i++) {
      const t = trail.length > 2 ? (i + 1) / (trail.length - 1) : 1;
      const opacity = 0.15 + (0.85 - 0.15) * t;
      lineTraces.push({
        type: "scatter",
        mode: "lines",
        x: [trail[i]!.x, trail[i + 1]!.x],
        y: [trail[i]!.y, trail[i + 1]!.y],
        // Plotly's line traces emit SVG paths with default round linecap/linejoin
        // — matches the visual spec's "round" requirement without explicit config.
        line: { color: tokens.accent, width: 1.4, shape: "linear" },
        opacity,
        hoverinfo: "skip",
        showlegend: false,
      });
    }

    // Marker (breadcrumb dot) trace — drop the newest point (under Current).
    const dots = trail.slice(0, -1);
    if (dots.length === 0) return lineTraces;
    const sizes: number[] = [];
    const opacities: number[] = [];
    for (let i = 0; i < dots.length; i++) {
      const t = dots.length > 1 ? i / (dots.length - 1) : 0;
      sizes.push(2.4 + (4.2 - 2.4) * t);
      opacities.push(0.20 + (0.78 - 0.20) * t);
    }
    const dotTrace: Partial<PlotlyTypes.PlotData> = {
      type: "scatter",
      mode: "markers",
      x: dots.map((p) => p.x),
      y: dots.map((p) => p.y),
      marker: {
        color: tokens.accent,
        // Plotly accepts number[] for size/opacity at runtime; cast through
        // unknown to satisfy @types/plotly.js's single-value union typings.
        size: sizes as unknown as number,
        opacity: opacities as unknown as number,
        line: { color: tokens.bgSunk, width: 1 },
      },
      hoverinfo: "skip",
      showlegend: false,
    };

    return [...lineTraces, dotTrace];
  }, [trail, tokens.accent, tokens.bgSunk]);

  // ── Comparison markers (parity with pre-Plotly component) ───────────────
  const comparisonTraces = comparisons.map((c) => ({
    type: "scatter" as const,
    mode: "markers" as const,
    x: [c.x],
    y: [c.y],
    text: [c.name],
    marker: { size: 12, color: c.color, opacity: 0.85, line: { width: 1.2, color: tokens.panel } },
    hovertemplate: "%{text}<extra></extra>",
    showlegend: false,
  }));

  // ── Shapes + annotations: iso-loss rings (T15) ───────────────────────────
  // Three-to-four concentric dashed circles anchored at (0, 0) — the Target
  // position under target_pca / taste_2d. Rendered via Plotly `shapes` with
  // layer: "below" so they sit behind all food dots and the path trail (T16).
  // Labels are Plotly annotations at (0, +r) with a bgSunk halo so the ring
  // dash "cuts" cleanly behind the label text.
  const shapes: PlotlyTypes.Shape[] = ringRadii.map((r) => ({
    type: "circle" as const,
    xref: "x" as const,
    yref: "y" as const,
    x0: -r, y0: -r, x1: r, y1: r,
    line: {
      color: tokens.ink3,
      width: 0.9,
      // Plotly's @types declare `dash` as a named-string union ("solid" |
      // "dot" | "dash" | ...) but the runtime accepts arbitrary
      // "<length> <gap>" SVG-style strings. The "2 4" pattern renders as
      // 2px-dash / 4px-gap. Cast through unknown to satisfy the type system.
      dash: "2 4",
    },
    opacity: 0.6,
    layer: "below" as const,
  } as unknown as PlotlyTypes.Shape));

  const ringAnnotations: Partial<PlotlyTypes.Annotations>[] = ringRadii.map((r) => ({
    x: 0,
    y: r,
    text: `L=${r}`,
    showarrow: false,
    font: { family: "Noto Sans Mono, ui-monospace, monospace", size: 9.5, color: tokens.ink3 },
    bgcolor: tokens.bgSunk,
    bordercolor: "rgba(0,0,0,0)",
    borderpad: 2,
    xanchor: "center" as const,
    yanchor: "middle" as const,
  }));

  const layout: Partial<PlotlyTypes.Layout> = {
    width,
    height,
    margin: { t: 14, b: 14, l: 14, r: 14 },
    paper_bgcolor: tokens.bgSunk,
    plot_bgcolor: tokens.bgSunk,
    showlegend: false,
    hovermode: "closest",
    dragmode: "pan",
    xaxis: {
      visible: true,
      showgrid: true,
      gridcolor: tokens.line2,
      griddash: "dot",
      gridwidth: 0.5,
      zeroline: false,
      showticklabels: false,
      ticks: "",
      showline: true,
      linecolor: tokens.line,
      linewidth: 1,
      mirror: true,
      range: xrange,
      fixedrange: false,
    },
    yaxis: {
      visible: true,
      showgrid: true,
      gridcolor: tokens.line2,
      griddash: "dot",
      gridwidth: 0.5,
      zeroline: false,
      showticklabels: false,
      ticks: "",
      showline: true,
      linecolor: tokens.line,
      linewidth: 1,
      mirror: true,
      range: yrange,
      fixedrange: false,
      scaleanchor: "x",
      scaleratio: 1,
    },
    shapes,
    annotations: ringAnnotations,
  };

  // Apply the pinned view range via Plotly.relayout when it changes. With
  // the React state above only updating on (a) first render, (b) empty →
  // non-empty transition, and (c) user pan/zoom, this effect fires only
  // on those events — NOT on every autosave's neighbor/current update.
  // That's the whole point: the user's view stays still while Current
  // moves inside it.
  useEffect(() => {
    if (plotRef.current) {
      void Plotly.relayout(
        plotRef.current,
        { "xaxis.range": xrange, "yaxis.range": yrange },
      );
    }
  }, [xrange[0], xrange[1], yrange[0], yrange[1]]);

  return (
    <Plot
      data={[
        // Trail sits in front of iso-loss rings (those are in layout.shapes
        // with layer:"below") but behind all food dots and markers.
        ...trailTraces,
        othersTrace,
        nearestTrace,
        leaderTrace,
        labeledTrace,
        labelTextTrace,
        targetHaloTrace,
        targetRingTrace,
        targetDotTrace,
        currentHaloTrace,
        currentTrace,
        ...comparisonTraces,
      ]}
      layout={layout}
      config={{
        displayModeBar: false,
        scrollZoom: true,
        responsive: true,
        staticPlot: false,
      }}
      onInitialized={(_fig, gd) => { plotRef.current = gd as unknown as HTMLDivElement; }}
      onUpdate={(_fig, gd) => { plotRef.current = gd as unknown as HTMLDivElement; }}
      onRelayout={handleRelayout}
      onClick={(e) => {
        if (!onFlavorClick) return;
        // Only neighbour traces carry customdata; target/current/comparison
        // markers have no customdata so we silently ignore those clicks.
        const point = e.points?.[0] as unknown as
          | { customdata?: string; text?: string }
          | undefined;
        const id = point?.customdata;
        if (!id) return;
        onFlavorClick({ id, name: point?.text ?? "" });
      }}
      style={{ width, height }}
    />
  );
}
